import React, { useState } from 'react';
import { Modal, TextField } from '@mui/material';
import { FormInputText } from '../../../../form-components/FormInputText';
import { useForm } from 'react-hook-form';
import CalendarIcon from '../../../CalendarIcon';
import { updateMemberTrialTicketData } from '../../../../api/ticket';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { errorMessages, validateDateRange } from '../../../../utils/methods';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import {
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  FormSection,
  FormRow,
  DatePickerSeparator,
  ButtonWrapper,
  CancelButton,
  SaveButton,
  PeriodFormLabel,
  AutoApplyButton,
  PeriodInputWrapper,
  PeriodDatePickerWrapper,
} from '../../ChangeTFreezyModalStyled';

export default function ChangeTrialPeriodModal({ modal, setModal, bigUser, ticket, user, getAllTickets, embedded = false, onDone }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues: ticket,
  });

  const watchedIssueDate = watch('issueDate');
  const watchedExpireDate = watch('expireDate');

  const isActive = watchedIssueDate && watchedExpireDate;

  // 기간제(PERIOD) 일일권은 횟수 개념이 없으므로 전체/남은 횟수 입력을 숨긴다.
  const isPeriodType = ticket?.usageType === 'PERIOD';

  // embedded: 상품 정보 수정 모달의 한 탭으로 렌더될 때 — 자체 Modal 껍데기 없이 본문만 그리고 닫기는 부모가 맡는다.
  const onClose = () => {
    if (embedded) {
      onDone?.();
      return;
    }
    setModal({ ...modal, open7: false });
  };

  const submit = async values => {
    const { expireDate, issueDate, memberTrialTicketId } = values;
    // 기간제는 횟수 입력이 숨겨져 있으므로 원본 ticket 값을 그대로 유지해 전송한다.
    const usageCount = values.usageCount ?? ticket?.usageCount;
    const remainingCount = values.remainingCount ?? ticket?.remainingCount;

    if (!issueDate) {
      toast.error('시작일자를 입력해주세요');
      return;
    }

    if (!expireDate) {
      toast.error('종료일자를 입력해주세요');
      return;
    }

    if (!validateDateRange(issueDate, expireDate)) {
      toast.error('기간을 다시 확인해주세요');
      return;
    }

    try {
      setIsSubmitting(true);
      await updateMemberTrialTicketData(
        user.memberId,
        memberTrialTicketId,
        {
          expireDate: moment(expireDate).format('YYYY-MM-DD'),
          startDate: moment(issueDate).format('YYYY-MM-DD'),
          usageCount,
          remainingCount,
        },
        bigUser.token,
      );

      // 목록 재조회를 기다린 뒤 닫아, 닫힌 화면에 갱신된 값이 바로 보이도록 한다.
      await getAllTickets();
      toast.success('변경이 완료되었습니다');
      onClose();
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePeriodChange = () => {
    // 일일권은 usagePeriodType이 없을 수 있으므로 기본 일(DAY) 기준으로 처리
    const periodType = ticket?.usagePeriodType || 'DAY';
    const period = ticket?.usagePeriod || 30;

    if (periodType === 'DAY') {
      setValue('expireDate', moment(watch('issueDate')).add(period, 'days').toDate());
    } else {
      setValue('expireDate', moment(watch('issueDate')).add(period, 'months').toDate());
    }
  };

  const body = (
    <ModalContent>
      <FormSection>
        {/* 이용 기간 */}
        <FormRow>
          <PeriodFormLabel>이용 기간</PeriodFormLabel>
          <PeriodDatePickerWrapper>
            <FormInputDate
              name="issueDate"
              control={control}
              placeholder="YYYY-MM-DD"
              components={{
                OpenPickerIcon: CalendarIcon,
              }}
              renderInput={params => (
                <TextField
                  sx={{
                    width: '163px',
                    '& .MuiOutlinedInput-root': {
                      height: '44px',
                      borderRadius: '8px',
                    },
                    '& .MuiSvgIcon-root': {
                      fill: '#0F7F85 !important',
                    },
                  }}
                  size="small"
                  {...params}
                />
              )}
            />
            <DatePickerSeparator>~</DatePickerSeparator>
            <FormInputDate
              name="expireDate"
              control={control}
              placeholder="YYYY-MM-DD"
              minDate={watchedIssueDate || null}
              disabled={!watchedIssueDate}
              components={{
                OpenPickerIcon: CalendarIcon,
              }}
              renderInput={params => (
                <TextField
                  sx={{
                    width: '163px',
                    '& .MuiOutlinedInput-root': {
                      height: '44px',
                      borderRadius: '8px',
                    },
                    '& .MuiSvgIcon-root': {
                      fill: '#0F7F85 !important',
                    },
                  }}
                  size="small"
                  {...params}
                />
              )}
            />
            <AutoApplyButton onClick={handlePeriodChange}>종료일 자동적용</AutoApplyButton>
          </PeriodDatePickerWrapper>
        </FormRow>

        {/* 전체/남은 횟수 — 횟수제(COUNT)일 때만 노출. 기간제(PERIOD)는 숨김 */}
        {!isPeriodType && (
          <>
            {/* 전체 횟수 */}
            <FormRow>
              <PeriodFormLabel>전체 횟수</PeriodFormLabel>
              <PeriodInputWrapper>
                <FormInputText
                  type="number"
                  control={control}
                  name="usageCount"
                  placeholder=""
                  rules={{
                    validate: value => {
                      if (value === '' || value === null || value === undefined)
                        return '값을 입력하세요';
                      if (!Number.isInteger(Number(value)) || Number(value) < 0)
                        return '0 이상의 정수를 입력하세요';
                      return true;
                    },
                  }}
                />
              </PeriodInputWrapper>
            </FormRow>

            {/* 남은 횟수 */}
            <FormRow>
              <PeriodFormLabel>남은 횟수</PeriodFormLabel>
              <PeriodInputWrapper>
                <FormInputText
                  type="number"
                  control={control}
                  name="remainingCount"
                  placeholder=""
                  rules={{
                    validate: value => {
                      if (value === '' || value === null || value === undefined)
                        return '값을 입력하세요';
                      if (!Number.isInteger(Number(value)) || Number(value) < 0)
                        return '0 이상의 정수를 입력하세요';
                      return true;
                    },
                  }}
                />
              </PeriodInputWrapper>
            </FormRow>
          </>
        )}
      </FormSection>

      <ButtonWrapper>
        <CancelButton onClick={onClose}>취소</CancelButton>
        <SaveButton
          isActive={isActive && !isSubmitting}
          disabled={!isActive || isSubmitting}
          onClick={handleSubmit(submit)}>
          저장
        </SaveButton>
      </ButtonWrapper>
    </ModalContent>
  );

  if (embedded) {
    return body;
  }

  return (
    <Modal
      open={modal.open7}
      onClose={onClose}
      aria-labelledby="trial-period-modal-title"
      aria-describedby="trial-period-modal-description">
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>일일권 기간/횟수 변경</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        {body}
      </ModalContainer>
    </Modal>
  );
}
